/** @format */
import {
	Component,
	Inject,
	Input,
	OnChanges,
	OnInit,
	PLATFORM_ID,
	SimpleChanges
} from '@angular/core';

import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs';

import { UtilityService } from '@shared/utils/services';
import { KillswitchService } from '@shared/data-access/services';
import {
	BVPixelResponse,
	ProductReviewDrawerType,
	ProductReviewed,
	Review,
	openProductReviewDrawer,
	selectBVPixelisAvailable,
	setReviewOnDrawer
} from '@product-reviews/data-access';

import { chain } from 'chains/chain';
import { BVProduct, BVTrackImpresionObject, RequiredSEOFields } from '../models';
import { isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'product-reviews-review-card',
    templateUrl: './review-card.component.html',
    styleUrls: ['./review-card.component.scss'],
    standalone: false
})
export class ReviewCardComponent implements OnInit, OnChanges {
	@Input() review: Review;
	@Input() product: ProductReviewed;
	starsOptions = {
		width: '16px',
		height: '16px'
	};
	rating = 0;
	hasNewReviewsEnhancement = false;
	hasRequiredValuesToSEO = false;
	requiredSEOFields = Object.values(RequiredSEOFields);
	selectBVData$ = this.store.select(selectBVPixelisAvailable);

	bvPixelResponse: BVPixelResponse;

	constructor(
		private store: Store,
		private readonly utilityService: UtilityService,
		private killSwitchService: KillswitchService,
		@Inject(PLATFORM_ID) private platformId
	) {}

	ngOnInit() {
		this.killSwitchService
			.getKillSwitchesEnvironment$()
			.pipe(take(1))
			.subscribe((data) => {
				this.hasNewReviewsEnhancement = this.utilityService.checkKillSwitch(
					'newReviewsEnhancement',
					data,
					chain.chainIdentifierAbbr
				);
			});

		if (isPlatformBrowser(this.platformId)) {
			this.selectBVData$
				.pipe(filter((resp: BVPixelResponse) => !!resp && resp.isBVPixelReady))
				.subscribe((bv: BVPixelResponse) => {
					this.bvPixelResponse = bv;
					this.handleBVPixel();
				});
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.review?.currentValue) {
			this.hasRequiredValuesToSEO = !this.hasRequiredSEOValues(this.review);
		}
	}

	openDrawer(review: string) {
		this.store.dispatch(
			setReviewOnDrawer({
				currentReview: review // TODO: verify ID of the review to pass to drawer, the open it
			})
		);

		const contentListDrawer = { drawerType: ProductReviewDrawerType.REVIEWS };
		this.store.dispatch(openProductReviewDrawer(contentListDrawer));
	}

	private hasRequiredSEOValues(review: Review): boolean {
		return this.requiredSEOFields.some((field) => {
			const value = review[field as keyof Review];
			return value === null || value === '';
		});
	}

	private handleBVPixel(): void {
		if (!!BV && BV.pixel) {
			const reviewImpressionData: BVTrackImpresionObject = {
				brand: this.bvPixelResponse.brand,
				bvProduct: BVProduct.RATINGSANDREVIEWS,
				contentId: this.review.Id,
				categoryId: this.bvPixelResponse.category,
				contentType: 'review',
				productId: this.bvPixelResponse.productId
			};

			BV.pixel.trackImpression(reviewImpressionData);
		}
	}
}
